import { useTranslation } from "react-i18next";
import { FactList, FactRow } from "@/components/fact-row";
import { TotalsLine, TotalsTotal } from "@/components/totals";
import { addInterval, formatLongDate } from "@/utils/date";
import { cadenceLine, firstPeriodLine } from "@/utils/interval";
import { formatTableMoney, fromCents } from "@/utils/money";
import type { AppliedPromo } from "./coupon-field";
import type {
  PublicCheckoutProduct,
  PublicCheckoutProductPrice,
} from "@/data/checkout";

/**
 * The totals (checkout 1.4 region `totals`; spec totals-row 1.0).
 *
 * The line item, the discount when a code was accepted, and one total. The discount is a
 * negative amount in the secondary ink and NOT in green: a reduction is a fact about the money,
 * not a success state, and semantic colour on a money surface is reserved for something being
 * wrong (gate 19.3).
 *
 * There is no VAT row. Neither the checkout payload nor the invoice payload carries the
 * workspace's tax status, so a "VAT 0.00 EUR" line would be a statement about a coach's tax
 * position invented to fill a shape the sheet happens to draw. Recorded as a backend gap.
 *
 * The caption under the total says what happens next and in whose money. The currency is the
 * ISO code and not `Intl.DisplayNames`: the display name is capitalised and singular in English
 * ("Euro") and reads wrong in half the six languages, while the three letters are the form the
 * money utility already uses in every row above it (rule 135). The next charge date is computed
 * from today plus the price's own interval, which is the same arithmetic Stripe will do.
 */

export interface CheckoutTotalsProps {
  products: PublicCheckoutProduct[];
  priceFor: (product: PublicCheckoutProduct) => PublicCheckoutProductPrice;
  mainPrice: PublicCheckoutProductPrice;
  isSubscription: boolean;
  currency: string;
  totalCents: number;
  promo: AppliedPromo | null;
  coachName: string;
}

export function CheckoutTotals({
  products,
  priceFor,
  mainPrice,
  isSubscription,
  currency,
  totalCents,
  promo,
  coachName,
}: CheckoutTotalsProps) {
  const { t } = useTranslation();
  const recurring = mainPrice.recurring ?? null;
  const money = (cents: number) => formatTableMoney(fromCents(cents), currency);

  const lineLabel =
    isSubscription && recurring
      ? firstPeriodLine(t, products[0]?.name ?? "", recurring)
      : (products[0]?.name ?? "");

  const grossCents = promo ? promo.originalAmountCents : totalCents;
  const discountCents = promo
    ? promo.originalAmountCents - promo.discountedAmountCents
    : 0;

  const nextDate =
    isSubscription && recurring
      ? formatLongDate(
          addInterval(
            new Date(),
            recurring.interval,
            recurring.intervalCount,
          ).toISOString(),
        )
      : null;

  return (
    <section
      className="gap-gk-3 flex flex-col"
      aria-label={t("gymkeePay.checkout.totalToday")}
    >
      {products.length > 1 ? (
        <FactList>
          {products.map((product) => (
            <FactRow
              key={product.id}
              label={product.name}
              value={money(priceFor(product).unitAmount)}
              numeric
            />
          ))}
        </FactList>
      ) : (
        <TotalsLine label={lineLabel} amount={money(grossCents)} />
      )}

      {promo && discountCents > 0 && (
        <TotalsLine
          label={t("gymkeePay.checkout.discountLine", { code: promo.code })}
          amount={`−${money(discountCents)}`}
        />
      )}

      <TotalsTotal
        label={t("gymkeePay.checkout.totalToday")}
        amount={money(totalCents)}
      />

      <p className="text-[13px] leading-[1.5] [overflow-wrap:anywhere] text-[var(--gk-text-tertiary)]">
        {isSubscription && recurring && nextDate
          ? `${t("gymkeePay.checkout.thenNext", {
              amount: money(promo ? promo.originalAmountCents : totalCents),
              cadence: cadenceLine(t, recurring),
              date: nextDate,
            })} ${t("gymkeePay.common.chargedIn", {
              currencyName: currency.toUpperCase(),
              coach: coachName,
            })}`
          : t("gymkeePay.common.chargedIn", {
              currencyName: currency.toUpperCase(),
              coach: coachName,
            })}
      </p>
    </section>
  );
}
